// Banner Appearance tab — layout, theme, brand color, preview studio constants.

export const BANNER_THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  GLASS: 'glass',
}

export const BANNER_TYPE = {
  BAR: 'bar',
  POPUP: 'popup',
}

export const BANNER_BAR_POSITION = {
  TOP: 'top',
  BOTTOM: 'bottom',
}

export const BANNER_POPUP_POSITION = {
  CENTER: 'center',
  TOP_LEFT: 'top-left',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_RIGHT: 'bottom-right',
}

/** Preview card device viewport (studio chrome, not banner placement). */
export const BANNER_APPEARANCE_VIEWPORT = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
}

/** Preview iframe harness lifecycle (`useBannerAppearancePreviewFrame`). */
export const BANNER_APPEARANCE_PREVIEW_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
}

/**
 * Logical CSS viewports inside the preview iframe (banner.js lays out against these).
 * Visual frame is scaled to fit the stage via useAppearancePreviewViewport.
 */
export const BANNER_PREVIEW_VIEWPORT_LOGICAL = {
  MOBILE_WIDTH: 390,
  MOBILE_HEIGHT: 780,
  MOBILE_SCALE_MIN: 0.42,
  MOBILE_SCALE_MAX: 0.82,
  DESKTOP_WIDTH: 1280,
}

/** Preview iframe mock page background (`data-bg` on preview-frame). */
export const BANNER_PREVIEW_DEMO_BACKGROUND = {
  LIGHT: 'light',
  DARK: 'dark',
  MIXED: 'mixed',
}

export const BANNER_THEME_OPTIONS = [
  { value: BANNER_THEME.LIGHT, label: 'Light', description: 'Clean white banner.' },
  { value: BANNER_THEME.DARK, label: 'Dark', description: 'Dark charcoal banner.' },
  {
    value: BANNER_THEME.GLASS,
    label: 'Glass',
    description: 'Frosted glass banner.',
    badge: 'Smart',
  },
]

export const BANNER_TYPE_OPTIONS = [
  { value: BANNER_TYPE.BAR, label: 'Bar', description: 'Full-width strip' },
  { value: BANNER_TYPE.POPUP, label: 'Popup', description: 'Floating card' },
]

export const BANNER_BAR_POSITION_OPTIONS = [
  { value: BANNER_BAR_POSITION.TOP, description: 'Top' },
  { value: BANNER_BAR_POSITION.BOTTOM, description: 'Bottom' },
]

export const BANNER_POPUP_POSITION_OPTIONS = [
  { value: BANNER_POPUP_POSITION.CENTER, description: 'Center' },
  { value: BANNER_POPUP_POSITION.TOP_LEFT, description: 'Top Left' },
  { value: BANNER_POPUP_POSITION.TOP_RIGHT, description: 'Top Right' },
  { value: BANNER_POPUP_POSITION.BOTTOM_LEFT, description: 'Bottom Left' },
  { value: BANNER_POPUP_POSITION.BOTTOM_RIGHT, description: 'Bottom Right' },
]

/** Brand accent swatches — cool → warm spectrum, neutrals last (for swatch grid rendering). */
export const BANNER_ACCENT_COLOR_OPTIONS = [
  { value: '#1e3a5f', label: 'Deep navy' },
  { value: '#0d47a1', label: 'Navy' },
  { value: '#1565c0', label: 'Material blue' },
  { value: '#1d4ed8', label: 'Blue' },
  { value: '#2563eb', label: 'Bright blue' },
  { value: '#0369a1', label: 'Sky' },
  { value: '#0891b2', label: 'Cyan' },
  { value: '#4338ca', label: 'Indigo' },
  { value: '#0f766e', label: 'Teal' },
  { value: '#059669', label: 'Emerald' },
  { value: '#15803d', label: 'Green' },
  { value: '#d97706', label: 'Amber' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#dc2626', label: 'Crimson' },
  { value: '#b91c1c', label: 'Red' },
  { value: '#e11d48', label: 'Rose' },
  { value: '#6d28d9', label: 'Violet' },
  { value: '#7c3aed', label: 'Purple' },
  { value: '#171717', label: 'Black' },
  { value: '#374151', label: 'Charcoal' },
  { value: '#475569', label: 'Slate' },
]

/** Default appearance assigned by the BE on banner creation. */
export const BANNER_DEFAULT_APPEARANCE = {
  theme: BANNER_THEME.LIGHT,
  type: BANNER_TYPE.BAR,
  position: BANNER_BAR_POSITION.BOTTOM,
  blocking: false,
  accentColor: '#1d4ed8',
  actions: { managePreferences: true },
  branding: true,
}

export const BANNER_PREVIEW_DEMO_THEME_OPTIONS = [
  { label: 'Light', value: BANNER_PREVIEW_DEMO_BACKGROUND.LIGHT, icon: 'sun' },
  { label: 'Dark', value: BANNER_PREVIEW_DEMO_BACKGROUND.DARK, icon: 'moon' },
  { label: 'Mixed', value: BANNER_PREVIEW_DEMO_BACKGROUND.MIXED, icon: 'sun-moon' },
]

/** Banner theme → preview mock page background (studio default pairing). */
export const BANNER_THEME_TO_PREVIEW_BACKGROUND = {
  [BANNER_THEME.LIGHT]: BANNER_PREVIEW_DEMO_BACKGROUND.LIGHT,
  [BANNER_THEME.DARK]: BANNER_PREVIEW_DEMO_BACKGROUND.DARK,
  [BANNER_THEME.GLASS]: BANNER_PREVIEW_DEMO_BACKGROUND.MIXED,
}

/** Appearance studio accordion — `key` matches the section component name. */
export const BANNER_APPEARANCE_STUDIO_SECTIONS = [
  {
    key: 'AppearanceLayoutSection',
    title: 'Layout',
    icon: 'layout-panel-top',
    defaultOpened: true,
  },
  {
    key: 'AppearanceThemeSection',
    title: 'Theme',
    icon: 'sun-moon',
    class: 'q-pt-md',
  },
  {
    key: 'AppearanceBrandColorSection',
    title: 'Brand color',
    icon: 'palette',
  },
  {
    key: 'AppearanceVisitorSection',
    title: 'Banner options',
    icon: 'mouse-pointer-click',
    showSummary: false,
    class: 'q-pt-md',
  },
]
